import type { Answers, AnswerValue } from './catalogue';
import type { AnswerChanges } from './api';

/**
 * The arithmetic of a draft that is saved while it is typed. Pure, so the cases
 * that lose answers in other applications — a save landing while the person keeps
 * typing, a second tab saving first — are tested without timers or a network.
 */

/** The server's answers with the edits not yet saved laid over them. */
export function withPending(saved: Answers, pending: AnswerChanges): Answers {
  const merged: Record<string, AnswerValue> = { ...saved };
  for (const [id, value] of Object.entries(pending)) {
    if (value === null) delete merged[id];
    else merged[id] = value;
  }
  return merged;
}

/**
 * What is still unsaved once a save of `sent` has succeeded: every edit made since
 * it was sent. An edit sent and then changed again stays pending with its newer
 * value; one sent and untouched since is done.
 */
export function stillPending(pending: AnswerChanges, sent: AnswerChanges): AnswerChanges {
  const left: Record<string, AnswerValue | null> = {};
  for (const [id, value] of Object.entries(pending)) {
    if (!(id in sent) || !same(sent[id] ?? null, value)) left[id] = value;
  }
  return left;
}

/**
 * The fields another session changed that this one is also changing, after a
 * version conflict: `before` is what this session last saw, `latest` what the
 * other one saved. These are the ones the person is told about, because their own
 * edit is about to overwrite somebody else's.
 */
export function overlapping(before: Answers, latest: Answers, pending: AnswerChanges): string[] {
  return Object.keys(pending).filter((id) => !same(before[id] ?? null, latest[id] ?? null));
}

export function same(a: AnswerValue | null, b: AnswerValue | null): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, index) => item === b[index]);
  }
  return a === b;
}

/** 1 s, 2 s, 4 s … to 30 s: patient with a phone on a train, not with a broken server. */
export function retryDelay(attempt: number): number {
  return Math.min(30_000, 1_000 * 2 ** attempt);
}
